<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import accountTemplatesAPI from 'dashboard/api/accountTemplates';
import Button from 'dashboard/components-next/button/Button.vue';

const emit = defineEmits(['close', 'created']);
const { t } = useI18n();

const step = ref(1);
const name = ref('');
const description = ref('');
const catalog = ref([]);
const chosen = ref({}); // { kind: Set(ids) }
const saving = ref(false);

const kindLabel = kind => t(`REVENDA.TEMPLATES.KINDS.${kind.toUpperCase()}`);
const groupTitle = group => `${kindLabel(group.kind)} (${group.items.length})`;
const isAll = group =>
  group.items.length > 0 &&
  chosen.value[group.kind]?.size === group.items.length;
const toggleAll = group => {
  chosen.value[group.kind] = isAll(group)
    ? new Set()
    : new Set(group.items.map(i => i.id));
};
const toggle = (kind, id) => {
  const set = new Set(chosen.value[kind] || []);
  if (set.has(id)) set.delete(id);
  else set.add(id);
  chosen.value[kind] = set;
};

const selection = computed(() =>
  Object.fromEntries(
    catalog.value
      .filter(group => chosen.value[group.kind]?.size)
      .map(group => [
        group.kind,
        isAll(group) ? 'all' : [...chosen.value[group.kind]],
      ])
  )
);
const totalChosen = computed(() =>
  Object.values(chosen.value).reduce((sum, set) => sum + set.size, 0)
);

const goToItems = async () => {
  step.value = 2;
  if (catalog.value.length) return;
  const { data } = await accountTemplatesAPI.catalog();
  catalog.value = data.catalog || [];
};

const create = async () => {
  saving.value = true;
  try {
    await accountTemplatesAPI.create({
      name: name.value,
      description: description.value,
      selection: selection.value,
    });
    emit('created');
  } catch (error) {
    useAlert(t('REVENDA.TEMPLATES.FAILED'));
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <woot-modal show :on-close="() => emit('close')">
    <div class="flex flex-col gap-4 p-6">
      <h3 class="text-lg font-medium text-n-slate-12">
        {{ t('REVENDA.TEMPLATES.NEW') }}
      </h3>
      <div v-if="step === 1" class="flex flex-col gap-3">
        <label>
          {{ t('REVENDA.TEMPLATES.NAME') }}
          <input v-model="name" type="text" />
        </label>
        <label>
          {{ t('REVENDA.TEMPLATES.DESCRIPTION_LABEL') }}
          <textarea v-model="description" rows="2" />
        </label>
      </div>
      <div
        v-else-if="step === 2"
        class="flex flex-col gap-3 max-h-96 overflow-auto"
      >
        <section
          v-for="group in catalog"
          :key="group.kind"
          class="p-3 border rounded-lg border-n-weak"
        >
          <label class="flex items-center gap-2 font-medium text-n-slate-12">
            <input
              type="checkbox"
              :checked="isAll(group)"
              :disabled="!group.items.length"
              @change="toggleAll(group)"
            />
            {{ groupTitle(group) }}
          </label>
          <label
            v-for="item in group.items"
            :key="item.id"
            class="flex items-center gap-2 ml-6 text-sm text-n-slate-11"
          >
            <input
              type="checkbox"
              :checked="chosen[group.kind]?.has(item.id)"
              @change="toggle(group.kind, item.id)"
            />
            {{ item.name }}
          </label>
        </section>
      </div>
      <div v-else class="text-sm text-n-slate-11">
        <p class="font-medium text-n-slate-12">{{ name }}</p>
        <p>{{ t('REVENDA.TEMPLATES.ITEMS_COUNT', { count: totalChosen }) }}</p>
      </div>
      <div class="flex justify-between">
        <Button
          v-if="step > 1"
          variant="ghost"
          :label="t('REVENDA.TEMPLATES.BACK')"
          @click="step -= 1"
        />
        <span v-else />
        <Button
          v-if="step === 1"
          :label="t('REVENDA.TEMPLATES.NEXT')"
          :disabled="!name.trim()"
          @click="goToItems"
        />
        <Button
          v-else-if="step === 2"
          :label="t('REVENDA.TEMPLATES.NEXT')"
          :disabled="!totalChosen"
          @click="step = 3"
        />
        <Button
          v-else
          :label="t('REVENDA.TEMPLATES.CREATE')"
          :is-loading="saving"
          @click="create"
        />
      </div>
    </div>
  </woot-modal>
</template>
